/**
 * 「タイピング完了 → リザルト表示」の間の取り方。
 *
 * 待ち時間 = max(基本の間, 単語音声の鳴り終わり)、ただし上限 `RESULT_MAX_DELAY_MS`。
 *
 * - **基本の間**: 打ち終わりのSE・+Nポップ(700ms)を見せてから切り替えるための余韻。
 *   これが無いと最後の1打と同時に画面が飛んで、何が起きたか分からない。
 * - **音声の鳴り終わり**: 英語モードは打ち終わりに単語の発音が鳴る(docs/05 §3c)。
 *   画面を切り替えると PlayScreen の後始末が `stopVoice()` するので、
 *   待たずに遷移すると最後の単語だけ発音が途中で切れてしまう。
 * - **上限**: 自動再生ブロック・`onended` が来ない端末で待ちっぱなしにしない安全弁。
 *
 * サーバー送信(submitStageResult)はこの待ちと**並行**に走らせること。
 * 直列にすると待ち時間が足し算になる(それが「クリア表示までが長い」の原因だった)。
 */

/** 演出の余韻。ここを伸ばすと全モードで遷移が遅くなる。 */
export const RESULT_BASE_DELAY_MS = 1000;
/** 音声を待つ上限。これ以上は鳴っていても切り替える。 */
export const RESULT_MAX_DELAY_MS = 4000;

/** 単語音声の終了通知(`SoundService.waitVoiceEnd`)。 */
export interface VoiceEndPort {
  waitVoiceEnd(): Promise<void>;
}

export interface ResultTransitionOptions {
  readonly baseDelayMs?: number;
  readonly maxDelayMs?: number;
  /** テスト差し替え用。既定は `setTimeout`。 */
  readonly schedule?: (callback: () => void, ms: number) => unknown;
}

/**
 * リザルトへ切り替えてよくなるまで待つ。
 * 音声ポートを渡さなければ基本の間だけ待つ(日本語モードで音声が無い場合など)。
 */
export function waitForResultTransition(
  voice?: VoiceEndPort | null,
  options: ResultTransitionOptions = {},
): Promise<void> {
  const base = options.baseDelayMs ?? RESULT_BASE_DELAY_MS;
  const max = options.maxDelayMs ?? RESULT_MAX_DELAY_MS;
  const schedule = options.schedule ?? ((callback, ms) => setTimeout(callback, ms));

  return new Promise<void>((resolve) => {
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      resolve();
    };

    // 上限。音声が終わらなくても必ずここで切り替える。
    schedule(finish, max);

    let elapsedBase = false;
    let voiceEnded = false;
    const bothReady = (): void => {
      if (elapsedBase && voiceEnded) finish();
    };
    schedule(() => {
      elapsedBase = true;
      bothReady();
    }, base);
    void (voice?.waitVoiceEnd() ?? Promise.resolve()).then(() => {
      voiceEnded = true;
      bothReady();
    });
  });
}
